import { useEffect, useMemo, useState } from "react";
import { Droplets, Sparkles, Package, Loader2, ClipboardCheck, AlertCircle, FileDown, FileText, TrendingUp, ArrowDown, ArrowUp, Search, CheckCircle2, Timer } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import { api, formatApiError } from "../lib/api";
import { fmtSf } from "../lib/utils";
import { toast } from "sonner";
import DateFilter from "./layout/DateFilter";
import PatchJobPerformanceCard from "./PatchJobPerformanceCard";

const ACCENT = "#A78BFA";

const STAGES = [
  { key: "wash",  label: "WASH",            Icon: Droplets, gap: 1 },
  { key: "patch", label: "PATCH",           Icon: Sparkles, gap: 3 },
  { key: "load",  label: "LOADING/CRATING", Icon: Package,  gap: 4 },
];

function fmtNum(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString();
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "2-digit" });
  } catch { return iso; }
}
function gapColor(gap, threshold) {
  if (gap === null || gap === undefined) return "text-muted-foreground";
  if (gap <= threshold) return "text-emerald-400";
  if (gap <= threshold * 2) return "text-amber-400";
  return "text-red-400";
}

const STATUS_BADGE = {
  PASS:      "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
  UNCHECKED: "bg-amber-500/10 border-amber-500/30 text-amber-400",
};


export default function FinishStagesSection({ showMexico = false }) {
  const [filter, setFilter] = useState({ preset: "all" });
  return (
    <section className="space-y-6" data-testid="finish-stages-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-bold text-xl">Finish Stages · Wash · Patch · Load</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Each stage shows live SF + Hours + a QC pipeline that flags pieces past their detection gap.
          </p>
        </div>
        <DateFilter value={filter} onChange={setFilter} />
      </div>
      {STAGES.map((s) => (
        <div key={s.key} className="space-y-3" data-testid={`finish-stage-block-${s.key}`}>
          <StageCard stage={s} filter={filter} showMexico={showMexico} />
          <StageDailyChartCard stage={s} parentFilter={filter} showMexico={showMexico} />
          <StageQcPipeline stage={s} filter={filter} showMexico={showMexico} />
          {/* Patch stage gets an extra piece-level performance card sourced
              from the "Patch Job Performance" sheet — placed directly below
              the Patch QC Pipeline per user spec. */}
          {s.key === "patch" && <PatchJobPerformanceCard showMexico={showMexico} />}
        </div>
      ))}
    </section>
  );
}


function StageCard({ stage, filter, showMexico }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    return p;
  }, [filter, showMexico]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/departments/FINISH/stage/${stage.key}`, { params });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [params, stage.key]);

  return (
    <section className="bg-[#12141D] border border-border overflow-hidden" data-testid={`finish-stage-${stage.key}-card`}>
      <span className="block h-[2px]" style={{ background: ACCENT }} />
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <stage.Icon size={16} style={{ color: ACCENT }} />
          <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">{stage.label}</span>
        </div>
        {loading || !data ? (
          <div className="py-6 flex items-center text-muted-foreground text-xs uppercase tracking-widest">
            <Loader2 size={14} className="animate-spin mr-2" /> Loading
          </div>
        ) : (
          <>
            <div className="flex items-end justify-between gap-3 mb-3">
              <div>
                <div className="font-heading font-bold text-3xl">{fmtSf(data.completed_sf)} <span className="text-base text-muted-foreground">SF</span></div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Completed</div>
              </div>
              <div>
                <div className="font-heading font-bold text-3xl tabular-nums">{data.percent}%</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1 text-right">Of {fmtSf(data.total_sf)} SF total</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <StageBlockTile label="Pieces" value={`${data.completed_pieces}/${data.total_pieces}`} />
              <StageBlockTile label="Hours" value={data.hours.toLocaleString()} />
              <StageBlockTile label="SF / Hour" value={data.sf_per_hour} accent />
            </div>
          </>
        )}
      </div>
    </section>
  );
}


function StageDailyChartCard({ stage, parentFilter, showMexico }) {
  // Independent date filter — initialised from parent's preset on first render,
  // then fully user-controlled. Each chart can be filtered independently.
  const [filter, setFilter] = useState(() => parentFilter || { preset: "all" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    return p;
  }, [filter, showMexico]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/departments/FINISH/stage/${stage.key}`, { params });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [params, stage.key]);

  const trend = data?.daily_trend || [];

  return (
    <section
      className="bg-[#12141D] border border-border overflow-hidden"
      data-testid={`finish-stage-${stage.key}-daily-chart`}
    >
      <span className="block h-[2px]" style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}33 100%)` }} />
      <div className="p-5">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} style={{ color: ACCENT }} />
            <h4 className="font-heading font-bold text-base">
              {stage.label} · Daily SF / Hour
            </h4>
          </div>
          <DateFilter value={filter} onChange={setFilter} />
        </div>

        {loading || !data ? (
          <div className="py-12 flex items-center justify-center text-muted-foreground text-xs uppercase tracking-widest">
            <Loader2 size={14} className="animate-spin mr-2" /> Loading
          </div>
        ) : trend.length === 0 ? (
          <div className="py-12 flex items-center justify-center text-muted-foreground text-xs uppercase tracking-widest">
            No data in selected range
          </div>
        ) : (
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <LineChart data={trend} margin={{ top: 4, right: 12, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} width={36} />
                <RTooltip
                  contentStyle={{ backgroundColor: "#12141D", border: "1px solid #374151", fontSize: 11 }}
                  labelStyle={{ color: "#E2E8F0" }}
                />
                <Line type="monotone" dataKey="sf_per_hour" stroke={ACCENT} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </section>
  );
}


function StageQcPipeline({ stage, filter: outerFilter, showMexico }) {
  // Local, fully interactive scope (mirrors Mold show pattern)
  const [filter, setFilter] = useState(() => outerFilter || { preset: "all" });
  const [data, setData] = useState({ rows: [], summary: {}, comments: [], gap_threshold: stage.gap });
  const [loading, setLoading] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const [status, setStatus] = useState("");
  const [comment, setComment] = useState("");
  const [sortBy, setSortBy] = useState("gap");
  const [sort, setSort] = useState("desc");
  const [search, setSearch] = useState("");

  const toggleSort = (key) => {
    setInteracted(true);
    setSortBy(key);
    setSort((prev) => (sortBy === key ? (prev === "desc" ? "asc" : "desc") : (key === "cast" ? "asc" : "desc")));
  };

  // Prefetch comments list once so the dropdown is populated even before interaction.
  useEffect(() => {
    api.get(`/departments/FINISH/stage/${stage.key}/pipeline-rows`,
            { params: { preset: "all", show_mexico: showMexico } })
      .then((r) => setData((d) => ({ ...d, comments: r.data.comments || [], gap_threshold: r.data.gap_threshold ?? stage.gap })))
      .catch(() => {});
  }, [stage.key, stage.gap, showMexico]);

  const params = useMemo(() => {
    const p = { preset: filter.preset, sort_by: sortBy, sort, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    if (status) p.status = status;
    if (comment) p.comment = comment;
    return p;
  }, [filter, status, comment, sortBy, sort, showMexico]);

  useEffect(() => {
    if (!interacted) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/departments/FINISH/stage/${stage.key}/pipeline-rows`, { params });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [params, interacted, stage.key]);

  const onFilterChange = (v) => { setInteracted(true); setFilter(v); };
  const onStatusChange = (v) => { setInteracted(true); setStatus(v); };
  const onCommentChange = (v) => { setInteracted(true); setComment(v); };

  const filteredRows = useMemo(() => {
    if (!search.trim()) return data.rows || [];
    const q = search.toLowerCase();
    return (data.rows || []).filter((r) =>
      (r.job || "").toLowerCase().includes(q) ||
      (r.job_name || "").toLowerCase().includes(q) ||
      (r.piece_mark || "").toLowerCase().includes(q) ||
      (r.piece_sn || "").toLowerCase().includes(q)
    );
  }, [data.rows, search]);

  const summary = data.summary || {};
  const coverageDays = summary.coverage_days ?? 0;
  const threshold = data.gap_threshold ?? stage.gap;

  const handleExport = (fmt) => {
    const qs = new URLSearchParams({ fmt, preset: filter.preset, show_mexico: String(showMexico), sort_by: sortBy, sort });
    if (filter.preset === "custom") { qs.set("start", filter.start); qs.set("end", filter.end); }
    if (status) qs.set("status", status);
    if (comment) qs.set("comment", comment);
    if (search && search.trim()) qs.set("search", search.trim());
    const token = localStorage.getItem("token");
    if (token) qs.set("token", token);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/departments/FINISH/stage/${stage.key}/export?${qs.toString()}`;
    window.location.href = url;
  };

  return (
    <section
      className="relative bg-[#12141D] border border-border overflow-hidden"
      data-testid={`finish-qc-${stage.key}`}
    >
      <span
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}66 100%)` }}
      />

      {/* Header + Date Filter */}
      <div className="px-5 py-4 border-b border-border flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ClipboardCheck size={18} style={{ color: ACCENT }} />
            <h3 className="font-heading font-bold text-xl">{stage.label}</h3>
            <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              · QC Pipeline · {summary.total ?? 0} pieces
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
              <span className="px-2 py-1 border border-border bg-[#0A0B10]">Coverage</span>
              <span
                className="px-2 py-1 text-[#0A0B10] font-mono font-semibold"
                style={{ backgroundColor: ACCENT }}
                data-testid={`finish-qc-${stage.key}-coverage`}
              >
                {coverageDays}d
              </span>
            </div>
            <button
              data-testid={`finish-qc-${stage.key}-csv`}
              onClick={() => handleExport("csv")}
              className="inline-flex items-center gap-1 px-2 py-1 border border-border bg-[#0A0B10] hover:border-[#A78BFA]/60 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-white transition-colors"
            >
              <FileDown size={11} /> CSV
            </button>
            <button
              data-testid={`finish-qc-${stage.key}-pdf`}
              onClick={() => handleExport("pdf")}
              className="inline-flex items-center gap-1 px-2 py-1 border border-border bg-[#0A0B10] hover:border-[#A78BFA]/60 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-white transition-colors"
            >
              <FileText size={11} /> PDF
            </button>
          </div>
        </div>
        <DateFilter value={filter} onChange={onFilterChange} />
      </div>

      {/* Summary tiles — mirror the Patch Job Performance card (Total / Passed
          / Unchecked / Pass Rate / Total SF / Avg Gap / Max Gap). Coverage
          days is intentionally omitted here — it's already shown as a badge
          in the header. */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-px bg-border" data-testid={`finish-qc-${stage.key}-summary`}>
        <Tile label="Total Pieces" value={interacted ? (summary.total ?? 0) : "—"} accent={ACCENT} testId={`finish-qc-${stage.key}-tile-total`} />
        <Tile label="Passed" value={interacted ? (summary.passed ?? 0) : "—"} accent="#34D399" testId={`finish-qc-${stage.key}-tile-passed`} icon={<CheckCircle2 size={12} />} />
        <Tile label="Unchecked" value={interacted ? (summary.unchecked ?? 0) : "—"} accent="#FBBF24" testId={`finish-qc-${stage.key}-tile-unchecked`} icon={<AlertCircle size={12} />} />
        <Tile label="Pass Rate" value={interacted ? `${(summary.pass_rate ?? 0).toFixed(1)}%` : "—"} accent="#10B981" testId={`finish-qc-${stage.key}-tile-pass-rate`} />
        <Tile label="Total SF"
              value={interacted ? Number(summary.total_sf ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 }) : "—"}
              accent="#00D4FF" testId={`finish-qc-${stage.key}-tile-total-sf`} />
        <Tile label="Avg Gap" value={interacted ? fmtNum(summary.avg_gap_days ?? 0) : "—"} accent="#00D4FF" testId={`finish-qc-${stage.key}-tile-avg-days`} icon={<TrendingUp size={12} />} sub="days" />
        <Tile label="Max Gap" value={interacted ? fmtNum(summary.max_gap_days ?? 0) : "—"} accent="#F87171" testId={`finish-qc-${stage.key}-tile-max-days`} icon={<Timer size={12} />} sub="days" />
      </div>

      {/* Filters bar */}
      <div className="px-5 py-3 border-b border-border flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid={`finish-qc-${stage.key}-search`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search job / piece..."
            className="w-full bg-[#0A0B10] border border-border pl-9 pr-3 py-1.5 text-xs focus:outline-none focus:border-[#A78BFA]"
          />
        </div>
        <select
          data-testid={`finish-qc-${stage.key}-status-filter`}
          value={status}
          onChange={(e) => onStatusChange(e.target.value)}
          className="bg-[#0A0B10] border border-border px-3 py-1.5 text-xs focus:outline-none focus:border-[#A78BFA]"
        >
          <option value="">All Status</option>
          <option value="PASS">PASS</option>
          <option value="UNCHECKED">UNCHECKED</option>
        </select>
        <select
          data-testid={`finish-qc-${stage.key}-comment-filter`}
          value={comment}
          onChange={(e) => onCommentChange(e.target.value)}
          className="bg-[#0A0B10] border border-border px-3 py-1.5 text-xs focus:outline-none focus:border-[#A78BFA] max-w-xs"
        >
          <option value="">All Comments</option>
          {data.comments?.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Empty state */}
      {!interacted && (
        <div className="px-5 py-16 text-center" data-testid={`finish-qc-${stage.key}-empty`}>
          <ClipboardCheck size={32} className="mx-auto text-muted-foreground/40 mb-3" />
          <div className="text-sm text-muted-foreground">Select a filter or date range to load pieces.</div>
          <div className="text-[10px] tracking-widest uppercase text-muted-foreground/60 mt-2">
            Use the date range, status, or comment filters above
          </div>
          <button
            data-testid={`finish-qc-${stage.key}-quick-30d`}
            onClick={() => { setInteracted(true); setFilter({ preset: "30d" }); }}
            className="mt-5 inline-flex items-center gap-2 text-[#0A0B10] font-semibold text-xs uppercase tracking-widest px-4 py-2 hover:opacity-90 transition-opacity"
            style={{ backgroundColor: ACCENT }}
          >
            Quick load · Last 30 days →
          </button>
        </div>
      )}

      {/* Table */}
      {interacted && (
        <div className="overflow-x-auto" data-testid={`finish-qc-${stage.key}-table-wrap`}>
          <table className="w-full text-left text-xs">
            <thead className="bg-[#1A1C28] text-[10px] uppercase tracking-widest text-[#94A3B8]">
              <tr>
                <th className="py-2.5 px-4">Job</th>
                <th className="py-2.5 px-4">Job Name</th>
                <th className="py-2.5 px-4">Piece Mark</th>
                <th className="py-2.5 px-4">Piece S/N</th>
                <th className="py-2.5 px-4">
                  <SortHeader label="Cast Date" sortKey="cast" active={sortBy} dir={sort} onClick={toggleSort} testid={`finish-qc-${stage.key}-th-cast`} />
                </th>
                <th className="py-2.5 px-4">Check Date</th>
                <th className="py-2.5 px-4 text-right">
                  <SortHeader label="Gap Days" sortKey="gap" active={sortBy} dir={sort} onClick={toggleSort} align="right" testid={`finish-qc-${stage.key}-th-gap`} />
                </th>
                <th className="py-2.5 px-4">Comments</th>
                <th className="py-2.5 px-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="py-12 text-center uppercase tracking-widest text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && filteredRows.length === 0 && (
                <tr><td colSpan={9} className="py-12 text-center uppercase tracking-widest text-muted-foreground">No pieces match the filters</td></tr>
              )}
              {!loading && filteredRows.map((r, i) => (
                <tr key={`${r.job}-${r.piece_sn}-${i}`} className="border-t border-border/40 hover:bg-[#1A1C28]/40">
                  <td className="py-2 px-4 font-mono" style={{ color: ACCENT }}>{r.job || "—"}</td>
                  <td className="py-2 px-4 max-w-[280px] truncate" title={r.job_name}>{r.job_name || "—"}</td>
                  <td className="py-2 px-4 font-mono">{r.piece_mark || "—"}</td>
                  <td className="py-2 px-4 font-mono text-muted-foreground">{r.piece_sn || "—"}</td>
                  <td className="py-2 px-4 font-mono">{fmtDate(r.cast_date)}</td>
                  <td className="py-2 px-4 font-mono">{fmtDate(r.check_date)}</td>
                  <td className={`py-2 px-4 font-mono text-right tabular-nums font-semibold ${gapColor(r.detection_gap_days, threshold)}`}
                      data-testid={`finish-qc-${stage.key}-gap-${i}`}>
                    {r.detection_gap_days === null || r.detection_gap_days === undefined ? "—" : fmtNum(r.detection_gap_days)}
                  </td>
                  <td className="py-2 px-4 max-w-[260px] truncate" title={r.comments}>
                    <span className="text-muted-foreground">{r.comments || "—"}</span>
                  </td>
                  <td className="py-2 px-4">
                    <span className={`inline-block text-[9px] tracking-widest uppercase px-2 py-0.5 border rounded-sm ${STATUS_BADGE[(r.status || "").toUpperCase()] || "border-border text-muted-foreground"}`}>
                      {r.status || "—"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {interacted && (
        <div className="px-5 py-2.5 border-t border-border text-[10px] uppercase tracking-widest text-muted-foreground flex items-center justify-between">
          <span>Showing {filteredRows.length} of {data.rows?.length ?? 0}</span>
          <span>Sort: Gap Days {sort === "desc" ? "descending" : "ascending"}</span>
        </div>
      )}
    </section>
  );
}


function Tile({ label, value, accent, sub, testId, icon }) {
  const display = (typeof value === "string" || value === "—") ? value : fmtNum(value);
  return (
    <div data-testid={testId} className="bg-[#12141D] p-4 flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
        {icon && <span style={{ color: accent }}>{icon}</span>}
        {label}
      </div>
      <div className="font-heading font-bold text-2xl tabular-nums" style={{ color: accent }}>
        {display}
      </div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function SortHeader({ label, sortKey, active, dir, onClick, align, testid, accent = "#A78BFA" }) {
  const isActive = active === sortKey;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onClick(sortKey)}
      className={`inline-flex items-center gap-1 uppercase tracking-widest text-[10px] transition-colors ${
        isActive ? "text-white" : "text-[#94A3B8] hover:text-white"
      } ${align === "right" ? "justify-end w-full" : ""}`}
    >
      <span>{label}</span>
      {isActive ? (
        dir === "desc"
          ? <ArrowDown size={11} style={{ color: accent }} />
          : <ArrowUp size={11} style={{ color: accent }} />
      ) : (
        <ArrowDown size={11} className="opacity-30" />
      )}
    </button>
  );
}

function StageBlockTile({ label, value, accent }) {
  return (
    <div className="bg-[#0A0B10] border border-border p-3">
      <div className="font-heading font-bold text-lg" style={accent ? { color: ACCENT } : {}}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}
